import { getFirmRisk, getRiskParameters } from './http/labRisk';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { FirmRiskResponse, RiskParametersResponse } from '@thinkalpha/locates-ws-client';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Read-only query factories and invalidation helpers for the LAB risk
 * settings. Writes live in the model and call the HTTP layer directly,
 * then invalidate through the helpers here.
 */

export const LAB_FIRM_RISK = 'lab-firm-risk';
export const LAB_RISK_PARAMETERS = 'lab-risk-parameters';

/**
 * Short stale window: risk settings change only through this page, and
 * every write invalidates explicitly, so refocus refetches would only
 * make the tables flicker.
 */
const STALE_TIME_MS = 30_000;

/** The firm's own rule plus per-category rule counts. Disabled until a firm is known. */
export const getFirmRiskQuery = (
    firmId: string | undefined,
): QueryObserverOptions<FirmRiskResponse, Error, FirmRiskResponse> => {
    return {
        queryKey: [LAB_FIRM_RISK, firmId ?? null],
        queryFn: () => getFirmRisk(firmId!),
        enabled: Boolean(firmId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Every parameter of one firm in a single list; the model classifies
 * them into the symbol / account / provider / advanced tabs client-side
 * so one cache entry serves all four.
 */
export const getRiskParametersQuery = (
    firmId: string | undefined,
): QueryObserverOptions<RiskParametersResponse, Error, RiskParametersResponse> => {
    return {
        queryKey: [LAB_RISK_PARAMETERS, firmId ?? null],
        queryFn: () => getRiskParameters(firmId!),
        enabled: Boolean(firmId),
        staleTime: STALE_TIME_MS,
    };
};

export const invalidateFirmRiskQuery = (queryClient: ReactBindings['QueryClient'], firmId: string): Promise<void> => {
    return queryClient.invalidateQueries({ queryKey: [LAB_FIRM_RISK, firmId] });
};

export const invalidateRiskParametersQuery = (
    queryClient: ReactBindings['QueryClient'],
    firmId: string,
): Promise<void> => {
    return queryClient.invalidateQueries({ queryKey: [LAB_RISK_PARAMETERS, firmId] });
};
